import { createContext, useContext, useState, useEffect } from 'react'
import { authAPI } from '../api/auth'

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isTokenExpired, setIsTokenExpired] = useState(false)

  useEffect(() => {
    const authenticated = authAPI.isAuthenticated()
    setIsAuthenticated(authenticated)
    setIsLoading(false)
  }, [])

  const login = async (email, password) => {
    try {
      const result = await authAPI.login(email, password)
      setIsAuthenticated(!!result.token)
      setIsTokenExpired(false)
      if (result.branding) {
        window.dispatchEvent(
          new CustomEvent('blacktape:branding-apply', { detail: result.branding })
        )
      } else {
        window.dispatchEvent(new Event('blacktape:branding-refresh'))
      }
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Login failed' }
    }
  }

  const logout = () => {
    authAPI.logout()
    setIsAuthenticated(false)
    setIsTokenExpired(false)
    window.dispatchEvent(new Event('blacktape:branding-refresh'))
  }

  /** Establish session from an already-stored token (e.g. impersonation exchange). */
  const adoptStoredSession = () => {
    const authenticated = authAPI.isAuthenticated()
    setIsAuthenticated(authenticated)
    setIsTokenExpired(false)
    return authenticated
  }

  const setTokenExpired = (expired) => {
    setIsTokenExpired(expired)
    if (expired) {
      setIsAuthenticated(false)
    }
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      login, 
      logout,
      adoptStoredSession,
      isTokenExpired,
      setTokenExpired
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
